import type { StaffRole } from "@lafayette/auth";
import { capabilityRoles, rolesHoldCapability, type StaffCapability } from "@lafayette/domain";

// The staff console reads the ONE capability table of the domain (`capabilityRoles`, `packages/domain/src/authorization.ts`). An area
// names the capabilities it needs (its flags) and nothing else: it holds no role list. A capability the domain renames or removes
// stops the build here, because a name is a `StaffCapability`. Presentation only: the API decides every request again (ADR 0012).

/** The capabilities behind one flag. A list is "any of": the flag is true when the roles hold one of them. */
export type CapabilityRule = StaffCapability | readonly StaffCapability[];
/** An area's flags: the name of each flag and the capability (or capabilities) it follows. */
export type CapabilityRules = Readonly<Record<string, CapabilityRule>>;
export type CapabilityFlags<Rules extends CapabilityRules> = {
  readonly [Flag in keyof Rules]: boolean;
};

const asList = (rule: CapabilityRule): readonly StaffCapability[] =>
  typeof rule === "string" ? [rule] : rule;

/** Whether the roles hold the capability, or one of the capabilities of a list. */
export function rolesHold(roles: readonly StaffRole[], rule: CapabilityRule): boolean {
  return asList(rule).some((capability) => rolesHoldCapability(roles, capability));
}

/** The flags of an area for the roles of the session: `{ read: true, manage: false }` and so on. */
export function capabilityFlags<const Rules extends CapabilityRules>(
  rules: Rules,
  roles: readonly StaffRole[],
): CapabilityFlags<Rules> {
  return Object.fromEntries(
    Object.entries(rules).map(([flag, rule]) => [flag, rolesHold(roles, rule)]),
  ) as CapabilityFlags<Rules>;
}

/** The roles that hold at least one of the capabilities, for the menu and the route guard (they take a role list). */
export function rolesWithCapability(
  ...capabilities: readonly StaffCapability[]
): readonly StaffRole[] {
  return [...new Set(capabilities.flatMap((capability) => capabilityRoles[capability]))];
}
